import { PAYMENT_METHOD, STATUS } from 'constants/index';
import { v4 as uuidv4 } from 'uuid';
import { fieldNamesMap, fieldNameToColumnAlphabetMap, numericFieldsMap, validationMap, validationFirstIndexMap } from './constants';

/**
 * This function will return rowsData for table
 * In this function previewConfigName is for which type of bulk upload is this ( orders / catalogues ) (So This will be fetched from route URL)
 * @param {*} previewConfigName ( Config name is which type of bulk upload is this ) ( orders / catalogues)
 * @returns
 */
const getUpdatedRowData = (row, tableColumns) => {
	const updatedRowData = { key: uuidv4() };

	let index = 0;
	for (const column of tableColumns) {
		const { dataIndex } = column;
		updatedRowData[dataIndex] = { value: row[index], errorMessage: null };
		index++;
	}

	return updatedRowData;
};

/**
 * This Function is used to fetch index of that row from where we have to perform any action ( above that index all rows is used for instructions)
 * In this function previewConfigName is for which type of bulk upload is this ( orders / catalogues ) (So This will be fetched from route URL)
 * @param {*} excelData ( Excel Data after reading excel file )
 * @param {*} previewConfigName ( Config name is which type of bulk upload is this ) ( orders / catalogues)
 * @returns
 */
const getFieldNamesRowIndex = (excelData, previewConfigName) => {
	try {
		for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
			const rowData = excelData[rowIndex];
			const shouldCheckCurrentRow = String(rowData[0])?.toLowerCase() === validationFirstIndexMap[previewConfigName];
			if (!shouldCheckCurrentRow) continue;

			const checkLengthOfFields = fieldNamesMap[previewConfigName].length === rowData.length;
			if (!checkLengthOfFields) continue;

			for (let colIndex = 0; colIndex < rowData.length; colIndex++) {
				const element = rowData[colIndex];
				if (element?.toLowerCase() !== fieldNamesMap[previewConfigName][colIndex]?.toLowerCase()) {
					throw new Error(`Excel upload failed. Missing column name ${fieldNamesMap[previewConfigName][colIndex]}!`);
				}
			}

			return rowIndex;
		}
		return 0;
	} catch (error) {
		console.log(error);
		throw error;
	}
};

/**
 * This function will return columnData and rowsData for table
 * In this function previewConfigName is for which type of bulk upload is this ( orders / catalogues ) (So This will be fetched from route URL)
 * @param {*} excelData ( Excel Data after reading excel file )
 * @param {*} previewConfigName ( Config name is which type of bulk upload is this ) ( orders / catalogues)
 * @returns
 */
export const sanitizeExcelData = (excelData, previewConfigName) => {
	try {
		const fieldNamesRowIndex = getFieldNamesRowIndex(excelData, previewConfigName);
		if (fieldNamesRowIndex == null) throw new Error('Excel upload failed. Missing column names!');

		const tableColumns = generateTableColumns(previewConfigName);
		const tableRows = excelData.slice(fieldNamesRowIndex + 1).map((row) => getUpdatedRowData(row, tableColumns));

		return { tableRows, tableColumns };
	} catch (error) {
		console.log(error);
		throw error;
	}
};

/**
 * This function will return columnData for table
 * In this function previewConfigName is for which type of bulk upload is this ( orders / catalogues ) (So This will be fetched from route URL)
 * @param {*} previewConfigName ( Config name is which type of bulk upload is this ) ( orders / catalogues)
 * @returns
 */
export const generateTableColumns = (previewConfigName) => {
	const fieldNames = Object.keys(fieldNameToColumnAlphabetMap[previewConfigName]);

	const fixedWidthFields = new Set([
		'id',
		'length',
		'breadth',
		'height',
		'weight',
		'isb2bOrder',
		'isD2ROrder',
		'itemQuantity',
		'discount',
		'itemPrice',
		'pincode',
		'shippingPrice'
	]);
	const tableColumns = fieldNames.map((fieldName) => ({
		title: <h6 style={{ textTransform: 'capitalize', margin: '0.5rem 1rem' }}>{fieldName}</h6>,
		dataIndex: fieldName,
		...(fixedWidthFields.has(fieldName) && { width: 120 }),
		...(fieldName === 'shippingPrice' && { width: 120 })
	}));

	return tableColumns;
};

export const shouldConvertToNumber = (fieldName, previewConfigName) => numericFieldsMap[previewConfigName].includes(fieldName);

export const sanitizeData = (currentPreviewItem, previewConfigName) => {
	const updatedPreviewData = currentPreviewItem.map((currentPreviewItem) => {
		const updatedCurrentPreviewItem = {};

		for (const [fieldName, fieldData] of Object.entries(currentPreviewItem)) {
			const updatedKeyName = fieldNameToColumnAlphabetMap[previewConfigName][fieldName];
			if (updatedKeyName) {
				const { value } = fieldData;
				if (fieldName === 'status') {
					updatedCurrentPreviewItem[updatedKeyName] = STATUS[value.toUpperCase()];
				} else if (fieldName === 'paymentMethod') {
					updatedCurrentPreviewItem[updatedKeyName] = PAYMENT_METHOD[value.toUpperCase()];
				} else if (fieldName === 'itemPrice') {
					updatedCurrentPreviewItem[updatedKeyName] = Number.isNaN(Number.parseInt(value)) ? null : Number(value);
				} else {
					updatedCurrentPreviewItem[updatedKeyName] = shouldConvertToNumber(fieldName, previewConfigName) ? Number(value) : value;
				}
			}
		}

		return updatedCurrentPreviewItem;
	});

	return updatedPreviewData;
};

/**
 * This function will use for validation of each rows and each fields
 * In this function previewConfigName is for which type of bulk upload is this ( orders / catalogues ) (So This will be fetched from route URL)
 * @param {*} previewConfigName ( Config name is which type of bulk upload is this ) ( orders / catalogues)
 * @param {*} previewData ( previewData contains all rows )
 * @returns
 */
export const validateData = (previewData, previewConfigName) => {
	let doesErrorExists = false;

	const validatedData = previewData.map((previewItem) => {
		const updatedItem = { ...previewItem };

		for (const [fieldName, fieldData] of Object.entries(previewItem)) {
			if (fieldName === 'key') continue; // no validation required for "key"

			const { value } = fieldData;

			const errorMessage = validationMap[previewConfigName] ? validationMap[previewConfigName](value, fieldName) : null;

			if (errorMessage) doesErrorExists = true;

			updatedItem[fieldName] = { value, errorMessage };
		}
		return updatedItem;
	});

	return { validatedData, doesErrorExists };
};

export const getFirstErrorField = (previewData) => {
	for (const rowData of previewData) {
		for (const [columnName, columnData] of Object.entries(rowData)) {
			if (columnData.errorMessage) {
				return { errorRowKey: rowData.key, errorColumnName: columnName };
			}
		}
	}

	return {};
};

// delay function for specific timer
export const delay = (timer = 2000) => {
	return new Promise((res, rej) => {
		setTimeout(() => {
			res(1);
		}, timer);
	});
};
